import { EmptyState, ErrorState, LoadingState } from '@/components/ui/async-state';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { AppScreen, MetaBadge } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import { useAuth } from '@/lib/auth/auth';
import { useWorkspaceMode } from '@/features/workspaces/useWorkspaceMode';
import { router, Redirect } from 'expo-router';
import { ArrowLeftIcon, CheckIcon, MapPinIcon, UserRoundIcon } from 'lucide-react-native';
import * as React from 'react';
import { Pressable, ScrollView, View } from 'react-native';

export default function WorkspacesScreen() {
  const session = useAuth((state) => state.session);
  const workspaceMode = useWorkspaceMode();
  const {
    currentBusinessMembership,
    currentContext,
    membershipsQuery,
    setBusinessContext,
    setPersonalContext,
  } = useAccountContext();

  if (!session) {
    return <Redirect href={{ pathname: '/sign-in', params: { next: '/workspaces' } }} />;
  }

  const isPersonalActive = currentContext?.kind === 'personal';

  return (
    <AppScreen>
      <ScrollView
        className="flex-1"
        contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 16, paddingBottom: 40 }}
        showsVerticalScrollIndicator={false}>

        {/* Nav row */}
        <View className="mb-6 flex-row items-center justify-between">
          <Button
            variant="ghost"
            onPress={() =>
              router.canGoBack() ? router.back() : router.replace('/settings' as never)
            }>
            <Icon as={ArrowLeftIcon} size={16} color="white" />
            <Text className="font-semibold">Back</Text>
          </Button>
          <MetaBadge
            label={currentContext?.kind === 'business' ? 'Venue active' : 'Personal active'}
            tone="warm"
          />
        </View>

        {/* Page title */}
        <View className="mb-6 gap-1">
          <Text variant="small" className="font-semibold uppercase tracking-widest text-muted-foreground">
            Account
          </Text>
          <Text className="text-2xl font-bold text-foreground">Switch venue</Text>
          {currentContext?.kind === 'business' && currentBusinessMembership ? (
            <Text variant="small" className="text-muted-foreground">
              Active: {currentBusinessMembership.organization_name}
            </Text>
          ) : null}
        </View>

        {/* Personal row */}
        <Pressable
          className="mb-6 flex-row items-center gap-3 rounded-2xl border p-4"
          style={{
            backgroundColor: isPersonalActive
              ? DESIGN_TOKENS.colors.surfaceRaised
              : DESIGN_TOKENS.colors.surfaceOverlay,
            borderColor: isPersonalActive
              ? DESIGN_TOKENS.colors.accentStrong
              : DESIGN_TOKENS.colors.border,
            borderWidth: isPersonalActive ? 1.5 : 1,
          }}
          onPress={() => {
            void setPersonalContext().then(() => {
              router.replace('/(tabs)/events');
            });
          }}>
          <View className="size-10 items-center justify-center rounded-xl bg-muted">
            <Icon as={UserRoundIcon} size={16} className="text-muted-foreground" />
          </View>
          <View className="flex-1 gap-0.5">
            <Text className="font-semibold text-foreground">Personal</Text>
            <Text variant="small" className="text-muted-foreground">
              Events, map, and your profile
            </Text>
          </View>
          {isPersonalActive ? (
            <Icon as={CheckIcon} size={16} className="text-accent" />
          ) : null}
        </Pressable>

        {/* Version gate */}
        {!workspaceMode.supportsOrgWorkspace ? (
          <View className="rounded-2xl border border-border bg-muted/30 p-4">
            <Text className="font-semibold text-foreground">Update required for venues</Text>
            <Text variant="small" className="mt-1 text-muted-foreground">
              This build is on {workspaceMode.currentVersion}. Team and venue access begins with{' '}
              {workspaceMode.minimumVersion}.
            </Text>
          </View>
        ) : null}

        {/* Venue groups */}
        {workspaceMode.orgAccessEnabled ? (
          <View className="gap-5">
            {membershipsQuery.isPending ? <LoadingState label="Loading your venues..." /> : null}
            {membershipsQuery.isError ? (
              <ErrorState
                body={getAPIErrorRecoveryCopy(
                  membershipsQuery.error,
                  'workspace',
                  'Unable to load venues.'
                )}
                onAction={() => membershipsQuery.refetch()}
              />
            ) : null}
            {!membershipsQuery.isPending &&
            !membershipsQuery.isError &&
            membershipsQuery.data?.memberships.length === 0 ? (
              <EmptyState
                title="No venue access yet"
                body="When you join a team or claim a venue, it will show up here."
              />
            ) : null}

            {membershipsQuery.data?.memberships.map((membership) => (
              <View key={membership.organization_id} className="gap-2">
                {/* Org label */}
                <View className="flex-row items-center justify-between px-1">
                  <Text variant="small" className="font-semibold uppercase tracking-widest text-muted-foreground">
                    {membership.organization_name}
                  </Text>
                  <Text variant="small" className="text-muted-foreground/60">
                    {membership.role}
                  </Text>
                </View>

                {/* Venue cards */}
                {membership.venues.length > 0 ? (
                  membership.venues.map((venue) => {
                    const isActive =
                      currentContext?.kind === 'business' &&
                      currentContext.activeOrgId === membership.organization_id &&
                      currentContext.activeVenueId === venue.venue_id;
                    return (
                      <Pressable
                        key={venue.venue_id}
                        className="flex-row items-center gap-3 rounded-2xl border p-4"
                        style={{
                          backgroundColor: isActive
                            ? DESIGN_TOKENS.colors.surfaceRaised
                            : DESIGN_TOKENS.colors.surfaceOverlay,
                          borderColor: isActive
                            ? DESIGN_TOKENS.colors.accentStrong
                            : DESIGN_TOKENS.colors.border,
                          borderWidth: isActive ? 1.5 : 1,
                        }}
                        onPress={() => {
                          void setBusinessContext(membership.organization_id, venue.venue_id).then(
                            () => {
                              router.replace('/(business)/home' as never);
                            }
                          );
                        }}>
                        <View className="size-10 items-center justify-center rounded-xl bg-primary/15">
                          <Icon as={MapPinIcon} size={16} className="text-primary" />
                        </View>
                        <View className="flex-1 gap-0.5">
                          <Text className="font-semibold text-foreground">{venue.venue_name}</Text>
                          <Text variant="small" className="capitalize text-muted-foreground">
                            {venue.subscription_plan}
                          </Text>
                        </View>
                        {isActive ? (
                          <Icon as={CheckIcon} size={16} className="text-accent" />
                        ) : null}
                      </Pressable>
                    );
                  })
                ) : (
                  <View className="rounded-2xl border border-dashed border-border p-4">
                    <Text variant="small" className="text-muted-foreground">
                      No venues assigned yet
                    </Text>
                  </View>
                )}
              </View>
            ))}
          </View>
        ) : null}
      </ScrollView>
    </AppScreen>
  );
}
